import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireOwner?: boolean;
  blockPrestador?: boolean;
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireOwner = false,
  blockPrestador = false,
}: ProtectedRouteProps) {
  const { user, isLoading, isAdmin, isOwner, isPrestador, isConsumer, needsOnboarding, accountDeactivated } = useAuthContext();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Consumers (área do cliente) não pertencem às rotas de escritório.
  if (isConsumer) {
    return <Navigate to="/app" replace />;
  }

  if (accountDeactivated) {
    return <Navigate to="/conta-desativada" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/conta/escolher" replace />;
  }

  // Block prestadores from accessing office routes
  if (blockPrestador && isPrestador) {
    return <Navigate to="/meus-servicos" replace />;
  }

  // Owner-only pages (billing, org deletion)
  if (requireOwner && !isOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Acesso Restrito</h1>
          <p className="text-muted-foreground">Esta página é restrita ao proprietário da organização.</p>
        </div>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Acesso Restrito</h1>
          <p className="text-muted-foreground">Esta página é restrita a administradores.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
